
import { Component, Prop, Vue } from "vue-property-decorator";

import Card from "@/components/card/Card.vue";
import CollapsibleDescription from "@/components/common/CollapsibleDescriptionNew.vue";
import CardBodyItem from "@/components/card/CardBodyItem.vue";
import ItemChipsWithMore from "@/components/common/ItemChipsWithMore.vue";
import Badge from "@/components/common/BadgeNew.vue";
import CustomRequestModal from "@/components/requests/CustomRequestModalNew.vue";
import S3Image from "@/components/common/s3-image.vue";
import { getStatusName } from "@/helpers/requests/getStatus";
import getFullName from "@/helpers/getFullName";
import { NO_INFORMATION } from "@/data/placeholders";
import {
  Custom_Request,
  Custom_Request_Skills,
  Skill,
  User,
} from "@/generated/graphql";
import BadgeColor from "@/entities/BadgeColor";
import { customRequestSkillsNumber } from "@/data/skillChipsNumber";
import CollapsibleDescriptionLength from "@/entities/CollapsibleDescriptionLength";
import SuitableResources from "@/components/requests/SuitableResourcesRequest.vue";
import UPDATE_CUSTOM_REQUEST from "@/apollo/requests/UpdateCustomRequest.gql";
import { CustomRequestStatus } from "@/entities/CustomRequestStatus";
import { NOTIFICATION } from "@/entities/Events";
import Message from "@/data/notificationsMessages";
import RouteNames from "@/entities/RouteNames";
import MoreActions from "@/components/common/MoreActionsClick.vue";
import RouteParams from "@/entities/RouteParams";
import { hslColorFromName } from "@/utils/hslColorFromName";

@Component({
  components: {
    SuitableResources,
    Card,
    MoreActions,
    CollapsibleDescription,
    CardBodyItem,
    ItemChipsWithMore,
    Badge,
    S3Image,
    CustomRequestModal,
  },
})
export default class CustomRequestCard extends Vue {
  @Prop({ type: Array, default: () => [] })
  readonly skills!: Custom_Request_Skills[];
  @Prop({ type: Object, required: true }) readonly request!: Custom_Request;
  @Prop({ type: Object, required: true }) readonly createdBy!: User;
  @Prop({ type: Boolean, required: true }) readonly isAdmin!: boolean;
  @Prop({ type: String, required: true }) readonly currentUserId!: string;
  @Prop({ type: String, required: true }) readonly companyId!: string;

  modalShown = false;
  collapsibleDescriptionLength = CollapsibleDescriptionLength;
  skillsNumber = customRequestSkillsNumber;

  get skillIds(): number[] {
    return this.requestSkills.map(({ id }: Skill) => id);
  }

  get excludeResourceUserId(): string {
    return this.request.project_activity?.contractor?.user_id;
  }

  get requestJobTitle(): string {
    return this.request.job_title || NO_INFORMATION;
  }

  get requestDescription(): string {
    return this.request.description || NO_INFORMATION;
  }

  get badgeColor(): string {
    switch (this.request.status) {
      case CustomRequestStatus.UNDER_REVIEW:
        return BadgeColor.INFO;
      case CustomRequestStatus.APPLICANTS_SENT:
        return BadgeColor.WARNING;
      default:
        return BadgeColor.SUCCESS;
    }
  }

  get initial(): string {
    const firstName = this.request.created_by?.given_name;
    const lastName = this.request.created_by?.family_name;

    if (!firstName && !lastName) {
      return ''
    }

    const givenName = firstName ? firstName.trimLeft()[0] : '';
    const familyName = lastName ? lastName.trimLeft()[0] : '';
    return `${givenName}${familyName}`;
  }

  get bgStyle(): object {
      return {
          background: hslColorFromName(this.initial || ""),
      };
  }

  get requestSkills(): Skill[] {
    return this.skills.map(({ skill }) => skill);
  }

  get avatarStyleClasses(): Record<string, boolean> {
    return {
      ["border border-primary"]: true,
    };
  }
  get defaultImageClasses(): string {
    return "text-storm-gray text-sm font-medium rounded-full border border-gray-300 bg-white w-6 h-6 mr-1";
  }
  get defaultInitialsClasses(): string {
    return "inline-flex h-6 w-6 rounded-full items-center justify-center";
  }

  get requestCreatorName(): string {
    const createdBy = this.request.created_by;
    return getFullName(createdBy.given_name!, createdBy.family_name!);
  }

  get createdByCurrentUser(): boolean {
    return this.request.created_by.id === this.currentUserId;
  }

  get requestCreatorLink(): RouteParams<{ id: string }> | undefined {
    const managerId = this.request.created_by.manager?.id;
    if (!managerId) {
      return undefined;
    }
    return {
      name: RouteNames.MANAGER_ABOUT,
      params: { id: managerId },
    };
  }

  get status(): string {
    return getStatusName(this.request.status);
  }

  toggleRequestModal(): void {
    this.modalShown = !this.modalShown;
  }

  handleRefetch(): void {
    this.$emit("refetch");
  }

  get isApplicantsSent(): boolean {
    return this.request.status == CustomRequestStatus.APPLICANTS_SENT;
  }

  get isUnderReview(): boolean {
    return this.request.status == CustomRequestStatus.UNDER_REVIEW;
  }

  get showStatusButtonForManager(): boolean {
    return !this.isAdmin && this.showResolveRequestButton;
  }

  get showStatusButtonForAdmin(): boolean {
    return this.isAdmin && this.isRequestActive;
  }

  get statusTextForAdmin(): string {
    switch (this.request.status) {
      case CustomRequestStatus.UNDER_REVIEW:
        return "Applicants Sent";
      case CustomRequestStatus.APPLICANTS_SENT:
        return "Resolved";
      default:
        return "";
    }
  }

  get requestLink(): RouteParams<{ id: string }> | undefined {
    // const this.request.id = this.this.request.id
    if (!this.request.id) {
      return undefined;
    }
    return {
      name: RouteNames.CUSTOM_REQUEST_DETAILS,
      params: { id: this.request.id },
    };
  }

  get isRequestActive(): boolean {
    return this.isApplicantsSent || this.isUnderReview;
  }

  get showResolveRequestButton(): boolean {
    return !this.isAdmin && this.isRequestActive;
  }

 message(): void {
    this.$router.push({
      name: RouteNames.REQUEST_CONVERSATIONS,
      params: {
        id: this.request.id,
      },
    });
  }

  async updateRequest(): Promise<void> {
    let status: string = "";

    if (!this.isAdmin) {
      status = CustomRequestStatus.RESOLVED;
    }

    if (this.isAdmin) {
      status = this.isUnderReview
        ? CustomRequestStatus.APPLICANTS_SENT
        : CustomRequestStatus.RESOLVED;
    }

    try {
      await this.$apollo.mutate({
        mutation: UPDATE_CUSTOM_REQUEST,
        variables: {
          set: {
            status,
            updated_by_id: this.currentUserId,
          },
          id: this.request.id,
        },
      });
      this.$emit("refetch");
    } catch (e) {
      this.$eventBus.emit(NOTIFICATION, Message.errorMessage(e.message));
    }
  }
}
